/** Static rashi page data shared by rashi helpers and page sections. */

import { planetMap } from "@ishvaram/domain-data/planets";

// ─── Life Problems ────────────────────────────────────────────────────────────

export interface LifeProblem {
  id: string;
  name_en: string;
  name_hi: string;
  icon: string;
  houses: number[];
  description_en: string;
  description_hi: string;
}

export const LIFE_PROBLEMS: LifeProblem[] = [
  {
    id: "love",
    name_en: "Love",
    name_hi: "प्रेम",
    icon: "❤️",
    houses: [5, 7],
    description_en: "Romance, relationships, and love life",
    description_hi: "रोमांस, रिश्ते और प्रेम जीवन",
  },
  {
    id: "career",
    name_en: "Career",
    name_hi: "करियर",
    icon: "💼",
    houses: [10, 6, 2],
    description_en: "Professional growth, job, and business",
    description_hi: "पेशेवर विकास, नौकरी और व्यापार",
  },
  {
    id: "health",
    name_en: "Health",
    name_hi: "स्वास्थ्य",
    icon: "🌿",
    houses: [1, 6, 8],
    description_en: "Physical and mental well-being",
    description_hi: "शारीरिक और मानसिक स्वास्थ्य",
  },
  {
    id: "marriage",
    name_en: "Marriage",
    name_hi: "विवाह",
    icon: "💍",
    houses: [7, 2, 4],
    description_en: "Marriage prospects and marital life",
    description_hi: "विवाह की संभावनाएं और वैवाहिक जीवन",
  },
  {
    id: "children",
    name_en: "Children",
    name_hi: "संतान",
    icon: "👶",
    houses: [5, 9],
    description_en: "Childbirth and progeny",
    description_hi: "संतान प्राप्ति और संत्ति",
  },
  {
    id: "bad_phase",
    name_en: "Difficult Period",
    name_hi: "कठिन समय",
    icon: "🌑",
    houses: [6, 8, 12],
    description_en: "Overcoming tough phases and challenges",
    description_hi: "कठिन समय और चुनौतियों पर विजय",
  },
  {
    id: "vastu",
    name_en: "Vastu",
    name_hi: "वास्तु",
    icon: "🏠",
    houses: [4, 2],
    description_en: "Home direction and vastu remedies",
    description_hi: "गृह दिशा और वास्तु उपाय",
  },
  {
    id: "education",
    name_en: "Education",
    name_hi: "शिक्षा",
    icon: "📚",
    houses: [4, 5, 9],
    description_en: "Academic success and learning",
    description_hi: "शैक्षिक सफलता और अध्ययन",
  },
  {
    id: "spiritual",
    name_en: "Spiritual Growth",
    name_hi: "आध्यात्मिक विकास",
    icon: "📿",
    houses: [9, 12, 5],
    description_en: "Spiritual journey and dharma",
    description_hi: "आध्यात्मिक यात्रा और धर्म",
  },
  {
    id: "quick_answer",
    name_en: "Quick Answers",
    name_hi: "त्वरित उत्तर",
    icon: "⚡",
    houses: [1, 7, 10],
    description_en: "Quick vedic answers for immediate questions",
    description_hi: "तुरंत प्रश्नों के लिए वैदिक उत्तर",
  },
];

export const PROBLEM_MAP: Record<string, LifeProblem> = Object.fromEntries(
  LIFE_PROBLEMS.map((p) => [p.id, p])
);

// ─── House Significations ─────────────────────────────────────────────────────

export const HOUSE_MEANINGS: Record<number, string> = {
  1: "Self, personality, health, vitality",
  2: "Wealth, family, speech, food",
  3: "Courage, siblings, communication, short travel",
  4: "Home, mother, education, property, vehicles",
  5: "Children, creativity, romance, intelligence, past merit",
  6: "Enemies, disease, debt, daily work, service",
  7: "Marriage, partnerships, business, public dealings",
  8: "Longevity, transformation, sudden events, inheritance",
  9: "Dharma, father, guru, fortune, long travel, higher learning",
  10: "Career, status, authority, public image, karma",
  11: "Gains, income, elder siblings, aspirations, social network",
  12: "Losses, expenses, foreign travel, spirituality, moksha",
};

// ─── Planet Significations for Problems ──────────────────────────────────────

const PLANET_PRESENTATION = {
  sun: {
    vedicName: "Surya",
    gemstone: "Ruby (Manik)",
    mantra: "Om Suryaya Namah",
  },
  moon: {
    vedicName: "Chandra",
    gemstone: "Pearl (Moti)",
    mantra: "Om Chandraya Namah",
  },
  mars: {
    vedicName: "Mangal",
    gemstone: "Red Coral (Moonga)",
    mantra: "Om Angarakaya Namah",
  },
  mercury: {
    vedicName: "Budha",
    gemstone: "Emerald (Panna)",
    mantra: "Om Budhaya Namah",
  },
  jupiter: {
    vedicName: "Guru",
    gemstone: "Yellow Sapphire (Pukhraj)",
    mantra: "Om Gurave Namah",
  },
  venus: {
    vedicName: "Shukra",
    gemstone: "Diamond (Heera)",
    mantra: "Om Shukraya Namah",
  },
  saturn: {
    vedicName: "Shani",
    gemstone: "Blue Sapphire (Neelam)",
    mantra: "Om Shanaischaraya Namah",
  },
} as const;

/**
 * `planetMap[id].day` is a canonical lowercase weekday IDENTIFIER ("sunday").
 * `PLANET_INFO` is the presentation projection — it already formats `name_en` —
 * and its `day` lands directly in English remedy prose ("chant ... 108 times
 * every {day}"), where an English weekday must be capitalised. Presenting the
 * raw id shipped "every sunday" on the compatibility pages. Capitalise here so
 * the canonical id stays lowercase and only the display copy changes.
 */
function displayWeekday(dayId: string): string {
  return dayId.charAt(0).toUpperCase() + dayId.slice(1);
}

export const PLANET_INFO: Record<
  string,
  { name_en: string; name_hi: string; gemstone: string; mantra: string; day: string }
> = Object.fromEntries(
  Object.entries(PLANET_PRESENTATION).map(([id, presentation]) => {
    const planet = planetMap[id];
    if (!planet) throw new Error(`Missing canonical planet profile: ${id}`);
    return [
      id,
      {
        name_en: `${planet.name_en} (${presentation.vedicName})`,
        name_hi: planet.name_hi,
        gemstone: presentation.gemstone,
        mantra: presentation.mantra,
        day: displayWeekday(planet.day),
      },
    ];
  })
);
